import { useLayoutEffect, useState } from 'react'

/**
 * How much of a bar graph's 100px bottom reserve is left for the legend under the x-axis
 * labels. The angled date labels take about 25px; the rest fits two wrapped legend rows.
 */
const LEGEND_ROOM = 60

/**
 * Watches the absolutely positioned legend row at the bottom of a bar graph and returns how
 * much taller it is than the room under the x-axis labels, so the graph can shrink its plot by
 * that much. Zero while the legend fits, so desktop layouts are unchanged; on a phone, where
 * the legend wraps onto several rows, the bars move up instead of sliding under it.
 *
 * `legendRef` is a callback ref: the graphs render nothing until they have a size, so the row
 * mounts after the first render.
 */
export function useLegendOverflow() {
  const [legend, setLegend] = useState<HTMLDivElement | null>(null)
  const [legendOverflow, setLegendOverflow] = useState(0)

  useLayoutEffect(() => {
    if (!legend || typeof ResizeObserver === `undefined`) return
    const measure = () => setLegendOverflow(Math.max(0, Math.ceil(legend.getBoundingClientRect().height) - LEGEND_ROOM))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(legend)
    return () => observer.disconnect()
  }, [legend])

  return { legendRef: setLegend, legendOverflow }
}
